import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fmt, priceLabel } from "../lib/api";
import { QuickView } from "./QuickView";

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const [quickView, setQuickView] = useState(false);
  const hasChoices = (product.options || []).some((o) => o.choices?.length > 0);

  const quickAdd = (e) => {
    e.preventDefault();
    if (hasChoices) {
      setQuickView(true);
    } else {
      addItem(product, 1, {}, []);
    }
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
    >
      <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.id}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="img-blend h-full w-full object-contain p-3 transition-transform duration-700 ease-out group-hover:scale-[1.06]"
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-2xl border border-brand-line bg-white font-display text-brand-ink/20">CannaCheap DC</div>
          )}
          <div className="absolute left-3 top-3 flex flex-col gap-1.5">
            {product.onSale && (
              <span data-testid={`badge-sale-${product.id}`} className="rounded-full bg-brand-gold px-3 py-1 text-[10px] font-display font-bold uppercase tracking-[0.15em] text-brand-forest">
                Sale
              </span>
            )}
            {!product.inStock && (
              <span className="rounded-full bg-brand-ink/80 px-3 py-1 text-[10px] font-display font-bold uppercase tracking-[0.15em] text-white">
                Sold Out
              </span>
            )}
          </div>
          <div className="absolute inset-x-3 bottom-3 flex gap-2 opacity-0 transition-all duration-300 group-hover:opacity-100 [@media(hover:none)]:hidden">
            <button
              data-testid={`quick-add-${product.id}`}
              onClick={quickAdd}
              disabled={!product.inStock}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-brand-forest/95 py-2.5 text-xs font-bold uppercase tracking-wider text-brand-cream backdrop-blur transition-colors hover:bg-brand-pine disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> {hasChoices ? "Options" : "Add"}
            </button>
            <button
              data-testid={`quick-view-${product.id}`}
              aria-label={`Quick view ${product.name}`}
              onClick={(e) => { e.preventDefault(); setQuickView(true); }}
              className="rounded-full bg-white/95 p-2.5 text-brand-forest backdrop-blur transition-colors hover:bg-white"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="pt-3.5">
          <h3 className="font-display text-[15px] font-bold leading-snug text-brand-ink transition-colors group-hover:text-brand-pine">
            {product.name}
          </h3>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-display text-base font-black text-brand-pine">{priceLabel(product)}</span>
            {product.comparePrice && (
              <span className="text-xs text-brand-ink/45 line-through">{fmt(product.comparePrice)}</span>
            )}
          </div>
        </div>
      </Link>
      <QuickView product={product} open={quickView} onClose={() => setQuickView(false)} />
    </motion.article>
  );
}
